/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents, EMPTY_INTRANET_TASK_PROGRESS } from '@gh/builder-shared-data';
import { ActionFlow, getRouteInitData, pollProgress } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { assign } from 'lodash';
import {
  isIntranetTaskInProgress,
  SystemVarsAccountsIntranet,
} from '../../../resources/model/system-vars-accounts.model';

import { SystemVarsAccountsTabBusinessService } from '../../../resources/system-vars-accounts-tab-business.service';
import { SystemVarsAccountsIntranetFormState } from './system-vars-accounts-intranet-form.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'gh-system-vars-accounts-nexmo-subtab',
  template: require('./system-vars-accounts-intranet-subtab.component.html'),
  styles: [require('./system-vars-accounts-intranet-subtab.component.scss')],
  viewProviders: [
    SystemVarsAccountsIntranetFormState,
  ],
})
export class SystemVarsAccountsIntranetSubtabComponent extends AbstractFormComponent<SystemVarsAccountsIntranet> implements OnInit, OnDestroy {
  private originalIntranet: SystemVarsAccountsIntranet;
  private progressSubscription: Subscription;

  constructor(
    public form: SystemVarsAccountsIntranetFormState,
    private route: ActivatedRoute,
    private systemVarsBusiness: SystemVarsAccountsTabBusinessService,
    private navigationService: BuilderNavigationService,
    private dialogService: DialogService,
    private pipelines: FormActionPipelines,
    private actions: CommonActions,
    private waitingService: WaitingService,
    private notifications: NotificationService,
  ) {
    super();
  }

  ngOnInit(): void {
    const { originalIntranet } = getRouteInitData(this.route);
    assign(this, { originalIntranet });
    this.form.build(originalIntranet);

    this.progressSubscription = pollProgress({
      empty: EMPTY_INTRANET_TASK_PROGRESS,
      isInProgress: isIntranetTaskInProgress,
      status: this.form.progress$,
      poll: () => this.systemVarsBusiness.getIntranetTaskProgress(),
    }).subscribe(({ nextStatus }) => {
        this.form.setTaskInProgress(nextStatus);
    });
  }

  ngOnDestroy(): void {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
  }

  get isIntranetConnected(): boolean {
    return Boolean(this.originalIntranet.loginGuid);
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsAccountsIntranet>> {
    return this.pipelines.complete({
      execute: this.actions.execute((state: FormFlowState<SystemVarsAccountsIntranet>) =>
        this.systemVarsBusiness.updateIntranet(state.form.submitValue)),
      finish: (state: FormFlowState<SystemVarsAccountsIntranet>) => {
        const { value } = state;
        this.originalIntranet = value;
        this.form.build(value);
      },
    });
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.build(this.originalIntranet);
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.IntegrationAccounts],
      label: label ? label : 'lbl_integration_accounts',
    });
  }

  sendJobsToIntranet(): void {
    this.systemVarsBusiness
      .updateIntranetManagerContractsUpdateBulkJob(this.form.submitValue).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (sync) => {
          this.form.setTaskInProgress(sync.progress);
        },
        this.notifications.processAll,
      );
  }
}
